import { gql } from '@apollo/client';
import type { PageableContent, UUID } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import queryString from 'query-string';
import type { Path } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type { Configuration } from '../../request/Types';
import type { PathAssociation, PathAssociationFilters, VehiclePathAssociationsFilters } from './Types';

export class PathAssociationRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getPathAssociations = async ({
    period,
    page,
    size,
    sort,
    wantedVehicleIds,
    wantedGroups,
    searchQuery,
  }: PathAssociationFilters): Promise<PageableContent<PathAssociation>> => {
    const params = queryString.stringify({
      begin: period.start,
      end: period.end,
      tz: TimeZone.getTimezone(),
      page,
      size,
      sort_order: sort?.order,
      search_query: searchQuery,
    });

    const wantedVehicleIdsParam = wantedVehicleIds?.length ? `&wanted_vehicle_ids=${wantedVehicleIds}` : '';
    const wantedGroupsParam = wantedGroups?.length ? `&wanted_vehicle_group_ids=${wantedGroups}` : '';

    const { data } = await this.requester.request({
      endpoint: `/costs/vehicle-routes?${params}${wantedVehicleIdsParam}${wantedGroupsParam}`,
      method: HttpMethod.GET,
    });

    return camelcaseKeys(data, { deep: true });
  };

  public getVehiclePathAssociations = async (vehicleId: UUID, { period, driverIds }: VehiclePathAssociationsFilters): Promise<PathAssociation> => {
    const params = queryString.stringify({
      vehicle_id: vehicleId,
      begin: period.start,
      end: period.end,
      tz: TimeZone.getTimezone(),
    });

    const { data } = await this.requester.request({
      endpoint: `/costs/vehicle-route?${params}`,
      method: HttpMethod.GET,
    });

    return camelcaseKeys(data[0], { deep: true });
  };

  public getPaths = async (vehicleId: UUID, startTime: number, endTime: number, associationMethods?: string[]): Promise<Path[]> =>
    graphQLRequester
      .query<{ paths: Path[] }>({
        query: gql`
      query GetPaths($vehicleId: ID!, $startTime: Float!, $endTime: Float!, $tz: String!, $associationMethods: [String]) {
        paths(vehicleId: $vehicleId, startTime: $startTime, endTime: $endTime, tz: $tz, associationMethods: $associationMethods) {
          associationSource
          driver {
            id
            name
          }
          endLatitude
          endLongitude
          endTime
          latitude
          longitude
          startLatitude
          startLongitude
          startTime
          type
          vehicle {
            id
            licensePlate
            model {
              brand
              name
            }
            groups {
              id
              name
            }
            driver {
              id
              name
            }
          }
        }
      }
    `,
        variables: { vehicleId, startTime, endTime, tz: TimeZone.getTimezone(), associationMethods },
      })
      .then((result) => result.paths);
}
